import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AppProviders } from './providers';
import { DeviceFrame } from './layout/DeviceFrame';
import { BootOverlay, SCREEN_ROOT_ID } from './boot/BootOverlay';
import { markScreenMounted } from './boot/bootStore';
import { ColdOpenProvider } from './coldOpen';
import { PaywallProvider } from '@/features/paywall/PaywallProvider';

import { ClientChatView, CopiedPhoneView, HomeView } from './routeComponents';

const loadHome = () => HomeView.preload();
const loadChat = () => ClientChatView.preload();
const loadPhone = () => CopiedPhoneView.preload();
// Dev previews are not part of the production bundle (they pull local episode JSON).
const DevRoutes = import.meta.env.DEV ? lazy(() => import('./routes/dev')) : () => null;

/**
 * Starts the landing route's chunk(s) right away, in parallel with the session bootstrap
 * (auth, catalog, episode), instead of after it: the splash covers both waits. The other route chunks
 * follow once the first screen is on, so later navigations never wait on the network.
 */
function prefetchLandingRoute() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  const split = window.innerWidth >= 1024; // DeviceFrame split layout mounts chat/home + phone
  const swallow = () => undefined;
  if (path === '/') void loadHome().catch(swallow);
  else if (path.startsWith('/play/phone')) { void loadPhone().catch(swallow); if (split) void loadChat().catch(swallow); }
  else if (path.startsWith('/play')) void loadChat().catch(swallow);
  if (split && path !== '/dev' && !path.startsWith('/dev/')) void loadPhone().catch(swallow);
}
prefetchLandingRoute();

function prefetchOtherRoutes() {
  const swallow = () => undefined;
  const run = () => { void loadHome().catch(swallow); void loadChat().catch(swallow); void loadPhone().catch(swallow); };
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 1500);
}

/** Rendered inside the route Suspense boundary: commits only once the first screen's chunks resolved. */
function ScreenMounted() {
  useEffect(() => { markScreenMounted(); prefetchOtherRoutes(); }, []);
  return null;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 60_000 } } });

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppProviders>
          <ColdOpenProvider>
          <PaywallProvider>
          <div id={SCREEN_ROOT_ID} className="h-full w-full">
          <Suspense fallback={null}>
            <Routes>
              <Route element={<DeviceFrame />}>
                <Route index element={<HomeView />} />
                <Route path="play" element={<ClientChatView />} />
                <Route path="play/phone" element={<CopiedPhoneView />} />
                <Route path="play/phone/:appID" element={<CopiedPhoneView />} />
              </Route>
              {import.meta.env.DEV ? <Route path="dev/*" element={<DevRoutes />} /> : null}
            </Routes>
            <ScreenMounted />
          </Suspense>
          </div>
          </PaywallProvider>
          </ColdOpenProvider>
        </AppProviders>
        <BootOverlay />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
